import { type ReactElement } from 'react';
import { CheckCircle2, Circle, Loader2, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';

import type { DeploymentStatus } from '../endpoints';
import { describeDeploymentStatus } from '../overrides';

/** Badge variant per deployment tone. */
function toneVariant(
  tone: ReturnType<typeof describeDeploymentStatus>['tone'],
): 'secondary' | 'outline' | 'destructive' {
  if (tone === 'success') return 'secondary';
  if (tone === 'destructive') return 'destructive';
  // progress + neutral both render as a subdued outline (progress adds a spinner).
  return 'outline';
}

/**
 * Deployment-status pill: ready ✓ (success), pulling/deploying/pending ⟳
 * (animated), stopped ◯ (neutral), failed_* ⚠ (destructive), wiping ⟳
 * (destructive). Renders nothing for a plain tenant (null status).
 */
export function DeploymentStatusPill({
  status,
}: {
  readonly status: DeploymentStatus | null;
}): ReactElement | null {
  const { t } = useTranslation();
  if (status === null) return null;

  const { tone, spin, i18nKey } = describeDeploymentStatus(status);
  const variant = toneVariant(tone);

  let Icon = Circle;
  if (spin) Icon = Loader2;
  else if (tone === 'success') Icon = CheckCircle2;
  else if (tone === 'destructive') Icon = AlertTriangle;

  return (
    <Badge variant={variant} className="gap-1">
      <Icon className={`h-3 w-3 ${spin ? 'animate-spin' : ''}`} aria-hidden="true" />
      {t(i18nKey)}
    </Badge>
  );
}
